import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Button, Grid, Card, CardContent, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Checkbox, FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Header from '../components/Header';

const SEVERITY_COLOR = { must_fix: '#d32f2f', should_fix: '#f57c00', nice_to_have: '#9e9e9e' };

const emptyProposal = { title: '', funder: '', submissionDate: '', deadline: '', status: 'draft', notes: '' };
const emptyFeedback = { proposalId: '', item: '', category: 'other', severity: 'should_fix', appliesToOtherFunders: false, owner: '' };

const GrantTracker = () => {
  const [proposals, setProposals] = useState([]);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [proposalForm, setProposalForm] = useState(emptyProposal);
  const [feedbackForm, setFeedbackForm] = useState(emptyFeedback);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      window.location.href = '/signin?redirect=/grants';
      return;
    }
    fetchProposals();
    fetchFeedback();
  }, []);

  const fetchProposals = async () => {
    const res = await fetch('/api/proposals');
    const data = await res.json();
    if (data.success) setProposals(data.proposals || []);
  };

  const fetchFeedback = async (proposalId) => {
    const url = proposalId ? `/api/feedback-items?proposalId=${proposalId}` : '/api/feedback-items';
    const res = await fetch(url);
    const data = await res.json();
    if (data.success) setFeedbackItems(data.feedbackItems || []);
  };

  const handleCreateProposal = async () => {
    const res = await fetch('/api/proposals', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(proposalForm)
    });
    const data = await res.json();
    if (data.success) {
      setProposals((prev) => [...prev, data.proposal]);
      setProposalForm(emptyProposal);
      setProposalDialogOpen(false);
    }
  };

  const handleCreateFeedback = async () => {
    const payload = { ...feedbackForm, proposalId: selectedProposal };
    const res = await fetch('/api/feedback-items', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (data.success) {
      setFeedbackItems((prev) => [data.feedbackItem, ...prev]);
      setFeedbackForm(emptyFeedback);
      setFeedbackDialogOpen(false);
    }
  };

  const otherFunderCandidates = feedbackItems.filter((f) => f.appliesToOtherFunders && f.status !== 'resolved');

  return (
    <Box>
      <Header cartQty={0} />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Grant Tracker</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setProposalDialogOpen(true)}>
            Add Proposal
          </Button>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {proposals.map((p) => (
            <Grid item xs={12} md={4} key={p.id}>
              <Card
                variant="outlined"
                sx={{ cursor: 'pointer', borderColor: selectedProposal === p.id ? '#1976d2' : undefined }}
                onClick={() => { setSelectedProposal(p.id); fetchFeedback(p.id); }}
              >
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{p.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{p.funder}</Typography>
                  <Chip label={p.status} size="small" sx={{ mt: 1 }} />
                  {p.deadline && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Deadline: {new Date(p.deadline).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {otherFunderCandidates.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: '#f57c00' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Flagged as applicable to other funders ({otherFunderCandidates.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Check these against any proposal currently in Draft before resubmission.
            </Typography>
          </Paper>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">
            Feedback {selectedProposal ? `for selected proposal` : '(all proposals)'}
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={!selectedProposal}
            onClick={() => setFeedbackDialogOpen(true)}
          >
            Add Feedback Item
          </Button>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Other funders?</TableCell>
                <TableCell>Owner</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feedbackItems.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>{f.item}</TableCell>
                  <TableCell>{f.category}</TableCell>
                  <TableCell>
                    <Chip label={f.severity.replace('_', ' ')} size="small"
                      sx={{ backgroundColor: SEVERITY_COLOR[f.severity], color: '#fff' }} />
                  </TableCell>
                  <TableCell>{f.status}</TableCell>
                  <TableCell>{f.appliesToOtherFunders ? 'Yes' : ''}</TableCell>
                  <TableCell>{f.owner}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      <Dialog open={proposalDialogOpen} onClose={() => setProposalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Proposal</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Title" value={proposalForm.title} onChange={(e) => setProposalForm({ ...proposalForm, title: e.target.value })} required fullWidth />
          <TextField label="Funder" value={proposalForm.funder} onChange={(e) => setProposalForm({ ...proposalForm, funder: e.target.value })} fullWidth />
          <TextField type="date" label="Submission date" InputLabelProps={{ shrink: true }} value={proposalForm.submissionDate} onChange={(e) => setProposalForm({ ...proposalForm, submissionDate: e.target.value })} fullWidth />
          <TextField type="date" label="Deadline" InputLabelProps={{ shrink: true }} value={proposalForm.deadline} onChange={(e) => setProposalForm({ ...proposalForm, deadline: e.target.value })} fullWidth />
          <TextField select label="Status" value={proposalForm.status} onChange={(e) => setProposalForm({ ...proposalForm, status: e.target.value })} fullWidth>
            {['draft', 'submitted', 'in_review', 'funded', 'declined'].map((s) => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
          </TextField>
          <TextField label="Notes" value={proposalForm.notes} onChange={(e) => setProposalForm({ ...proposalForm, notes: e.target.value })} multiline rows={2} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProposalDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateProposal} disabled={!proposalForm.title}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={feedbackDialogOpen} onClose={() => setFeedbackDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Feedback Item</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Feedback item" value={feedbackForm.item} onChange={(e) => setFeedbackForm({ ...feedbackForm, item: e.target.value })} required multiline rows={2} fullWidth />
          <TextField select label="Category" value={feedbackForm.category} onChange={(e) => setFeedbackForm({ ...feedbackForm, category: e.target.value })} fullWidth>
            {['methodology', 'budget', 'team', 'impact', 'timeline', 'writing', 'other'].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField select label="Severity" value={feedbackForm.severity} onChange={(e) => setFeedbackForm({ ...feedbackForm, severity: e.target.value })} fullWidth>
            {['must_fix', 'should_fix', 'nice_to_have'].map((s) => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
          </TextField>
          <TextField label="Owner" value={feedbackForm.owner} onChange={(e) => setFeedbackForm({ ...feedbackForm, owner: e.target.value })} fullWidth />
          <FormControlLabel
            control={<Checkbox checked={feedbackForm.appliesToOtherFunders} onChange={(e) => setFeedbackForm({ ...feedbackForm, appliesToOtherFunders: e.target.checked })} />}
            label="Applies to other funders"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFeedback} disabled={!feedbackForm.item}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GrantTracker;
